import { Base } from '@studiometa/js-toolkit';
import { gsap } from 'gsap';
import Card from './Card.js';

const random = gsap.utils.random;

/**
 * @typedef {{
 *   width: number,
 *   height: number,
 *   ratio: number,
 *   orientation: 'square' | 'landscape' | 'portrait',
 *   breakpoint: string,
 *   breakpoints: string[],
 *   activeBreakpoints: Record<string, boolean>
 * }} ResizeServiceProps
 */

/**
 * @typedef {{
 *   $refs: {
 *     container: HTMLElement,
 *     deck: HTMLElement[],
 *     nextBtn: HTMLButtonElement,
 *     wishlistLink: HTMLAnchorElement
 *   },
 *   $children: {
 *     Card: Card[]
 *   },
 *   $options: {
 *     duration: number,
 *     stagger: number,
 *     rotationRange: number,
 *     offsetRange: number
 *   }
 * }} DeckProps
 */

/**
 * Deck component - manages a collection of cards.
 * @augments {Base<DeckProps>}
 */
export default class Deck extends Base {
  static config = {
    name: 'Deck',
    refs: ['container', 'deck[]', 'nextBtn', 'wishlistLink'],
    emits: ['dealt'],
    components: {
      Card,
    },
    options: {
      duration: { type: Number, default: 2 },
      stagger: { type: Number, default: 0.07 },
      rotationRange: { type: Number, default: 8 },
      offsetRange: { type: Number, default: 8 },
    },
  };

  hasDealt = false;
  isMobile = true;

  /**
   * Get the number of revealed cards.
   * @returns {number}
   */
  get revealedCount() {
    return this.$children.Card.filter((card) => card.isRevealed).length;
  }

  /**
   * Check if all cards have been revealed.
   * @returns {boolean}
   */
  get allRevealed() {
    return this.$children.Card.every((card) => card.isRevealed);
  }

  /**
   * Get the next unrevealed card.
   * @returns {Card | undefined}
   */
  getNextCard() {
    return this.$children.Card.find((card) => !card.isRevealed);
  }

  /**
   * Scroll to the next unrevealed card (mobile only).
   */
  scrollToNextCard() {
    const nextCard = this.getNextCard();
    if (!nextCard) return;

    const deck = nextCard.$el.closest('.deck');
    if (deck && this.$refs.container) {
      this.$refs.container.scrollTo({
        left: /** @type {HTMLElement} */ (deck).offsetLeft,
        behavior: 'smooth',
      });
    }
  }

  /**
   * Update the visibility of navigation buttons.
   */
  updateButtons() {
    const { nextBtn, wishlistLink } = this.$refs;

    if (this.allRevealed) {
      wishlistLink?.classList.remove('opacity-0', 'pointer-events-none');
      wishlistLink?.classList.add('z-50');
      nextBtn?.classList.add('opacity-0', 'pointer-events-none');
    } else if (this.isMobile && nextBtn) {
      nextBtn.classList.remove('opacity-0', 'pointer-events-none');
      nextBtn.classList.add('z-50');
    }
  }

  /**
   * Handle resize events to track breakpoint changes.
   * @param {ResizeServiceProps} props Resize service props.
   */
  resized(props) {
    this.isMobile = !props.activeBreakpoints.md;
  }

  /**
   * Handle card activation event.
   */
  onCardActivated() {
    this.updateButtons();
  }

  /**
   * Handle click on the next button.
   */
  onNextBtnClick() {
    this.scrollToNextCard();
    this.$refs.nextBtn?.classList.add('opacity-0', 'pointer-events-none');
  }

  /**
   *
   */
  mounted() {
    this.dealCards();
  }

  /**
   *
   */
  dealCards() {
    if (this.hasDealt) return;
    this.hasDealt = true;

    const { deck: decks } = this.$refs;
    if (!decks || decks.length === 0) return;

    document.documentElement.classList.add('overflow-hidden');
    document.body.classList.add('overflow-hidden');

    const { duration: totalDuration, stagger, rotationRange, offsetRange } = this.$options;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    /** @type {{ card: Element, isMain: boolean, deckIndex: number }[]} */
    const allCards = [];

    decks.forEach((deck, deckIndex) => {
      const cardsBehind = deck.querySelectorAll('.card-behind');
      const mainCard = deck.querySelector('.card:not(.card-behind)');

      cardsBehind.forEach((card) => {
        allCards.push({ card, isMain: false, deckIndex });
      });

      if (mainCard) {
        allCards.push({ card: mainCard, isMain: true, deckIndex });
      }
    });

    for (let i = allCards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allCards[i], allCards[j]] = [allCards[j], allCards[i]];
    }

    allCards.forEach((cardInfo, index) => {
      const { card, isMain } = cardInfo;
      const finalRotation = isMain ? 0 : random(-rotationRange, rotationRange);
      const finalX = isMain ? 0 : random(-offsetRange, offsetRange);
      const finalY = isMain ? 0 : random(-offsetRange, offsetRange);
      const startX = random(-viewportWidth * 0.5, viewportWidth * 0.5);
      const startRotation = random(-35, 35);
      const delay = index * stagger;
      const remainingTime = totalDuration - delay;
      const duration = Math.max(0.6, remainingTime);
      const isLastCard = index === allCards.length - 1;

      gsap.fromTo(
        card,
        {
          opacity: 1,
          x: startX,
          y: viewportHeight + random(50, 150),
          rotateZ: startRotation,
          scale: 0.85,
          visibility: 'visible',
        },
        {
          x: finalX,
          y: finalY,
          rotateZ: finalRotation,
          scale: 1,
          duration,
          delay,
          ease: 'power3.out',
          onComplete: () => {
            if (isMain) {
              gsap.set(card, { clearProps: 'all' });
            }
            /** @type {HTMLElement} */ (card).classList.remove('card-hidden');
            if (isLastCard) {
              this.onDealComplete();
            }
          },
        },
      );
    });
  }

  /**
   *
   */
  onDealComplete() {
    document.documentElement.classList.remove('overflow-hidden');
    document.body.classList.remove('overflow-hidden');
    this.$refs.container?.classList.add('is-dealt');
    this.$emit('dealt');
  }
}
